/**
 * OfficerBioDialog Component
 *
 * Displays full officer biography in a modal dialog with achievements and affiliations
 */

import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { Officer } from '@/data/officers'

export interface OfficerBioDialogProps {
  /**
   * Officer profile to display
   */
  officer: Officer | null

  /**
   * Whether the dialog is open
   */
  open: boolean

  /**
   * Callback when dialog is closed
   */
  onClose: () => void
}

/**
 * OfficerBioDialog Component
 */
export const OfficerBioDialog: React.FC<OfficerBioDialogProps> = ({
  officer,
  open,
  onClose,
}) => {
  if (!officer) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="officer-bio-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      {/* Dialog Title */}
      <DialogTitle
        id="officer-bio-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 700, color: 'primary.main' }}
          >
            {officer.name}
          </Typography>
          <Typography variant="h6" sx={{ color: 'secondary.main', mt: 0.5 }}>
            {officer.title}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close dialog"
          onClick={onClose}
          sx={{
            color: 'grey.500',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent dividers sx={{ py: 3 }}>
        {/* Biography */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Biography
          </Typography>
          {officer.biography.split('\n\n').map((paragraph, index) => (
            <Typography
              key={index}
              variant="body1"
              paragraph
              sx={{
                lineHeight: 1.7,
                color: 'text.primary',
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Key Achievements */}
        {officer.keyAchievements && officer.keyAchievements.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: 'primary.main' }}
            >
              Key Achievements
            </Typography>
            <List
              sx={{
                listStyleType: 'disc',
                pl: 3,
                '& .MuiListItem-root': {
                  display: 'list-item',
                  py: 0.5,
                  px: 0,
                },
              }}
            >
              {officer.keyAchievements.map((achievement, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={achievement}
                    primaryTypographyProps={{
                      variant: 'body1',
                      color: 'text.primary',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Professional Affiliations */}
        {officer.affiliations && officer.affiliations.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, color: 'primary.main' }}
            >
              Professional Affiliations
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {officer.affiliations.join(' • ')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default OfficerBioDialog
